import _concat from 'lodash/concat';
import _filter from 'lodash/filter';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _map from 'lodash/map';
import _without from 'lodash/without';
import dayjs from '../../../utils/dayjs';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import {
  calculateAssignmentsTotals,
  extractSettingAs,
  getCalculatedGrade,
  getTotals,
  groupAssignmentsByCategory,
  round,
  toolIsManuallyGraded,
  formatDisplayGrade,
  formatDisplayPoints,
  GRADEBOOK_SETTINGS,
} from '@macmillan-learning/gradebook-shared';
import { getActivityVisibilityStatus } from '../../../utils/activity';
import s from './StudentGradebook.module.css';
import AssignmentHeaderLabelAndTool from '../../../components/AssignmentHeaderLabelAndTool';
import CategoryNameButton from '../../CategoryNameButton';
import DateCompleted from '../../DateCompleted';
import { formatDisplayPercentage } from '../../../utils/InstructorGradebookHelpers';
import { GRADING_TYPE, VISIBILITY_OPTIONS } from '../../../utils/constants';
import { emptyAlert, sortAssignmentsByISO } from '../../../utils/util';
import MLLoading from '../../MLLoading/MLLoading';
import MLAlert from '../../MLAlert/MLAlert';
import MLButton from '../../MLButton/MLButton';
import MLIcon from '../../MLIcon/MLIcon';
import UngradedIcon from '../../UngradedIcon';
import WeeklyProgress from '../../WeeklyProgress';
import StudentGoalReflectionContainer from '../../StudentGoalReflectionContainer';
import { getConfigObject } from '../../../utils/config';

const MOBILE_WIDTH = 1030;

class StudentGradebook extends Component {
  static propTypes = {
    studentGradebook: PropTypes.object,
    assignments: PropTypes.array,
    rdxGradebookSettings: PropTypes.array,
    settings: PropTypes.array,
    showGradebookCommentDialog: PropTypes.func,
    showEditStudentId: PropTypes.func,
    user: PropTypes.object,
    alert: PropTypes.object,
    currentStudent: PropTypes.object,
    categories: PropTypes.array,
    isGradebookFetching: PropTypes.bool,
    isGradebookReFetching: PropTypes.bool,
    gradingType: PropTypes.number,
    roundPercentType: PropTypes.number,
    categoryWeights: PropTypes.array,
    course: PropTypes.object,
    courseId: PropTypes.string,
    updateAlert: PropTypes.func,
    updateStudent: PropTypes.func,
    unlinkReefStudent: PropTypes.func,
    getLaunchUrl: PropTypes.func,
    handleLocalLaunch: PropTypes.func,
  };

  static defaultProps = {
    studentGradebook: {},
    assignments: [],
    user: {},
  };
  state = { width: window.innerWidth };

  static getStudentPointDisplay({ studentPoints, status, tool, subtype }) {
    if (status === 'Missed') {
      return toolIsManuallyGraded(tool, subtype) ? '--' : 0;
    } else if (studentPoints === null) {
      return '-';
    }
    return studentPoints;
  }

  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
  }

  getAssignmentPointPercentOfCourse(assignment, assignments) {
    const { pointsPossible } = calculateAssignmentsTotals(assignments);
    return getCalculatedGrade(pointsPossible, assignment.assigned_points);
  }

  getAssignmentCategoryName(assignment) {
    if (assignment.grading_type === GRADING_TYPE.UNGRADED) {
      return 'Ungraded';
    } else if (assignment.gradebook_category_id > 0) {
      return assignment.gradebook_category_name;
    }
    return 'Assignments';
  }

  areWeightsEnabled() {
    const { gradingType } = this.props;
    return gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted;
  }

  updateWindowDimensions = () => {
    this.setState({ width: window.innerWidth });
  };

  isFetching = () => {
    const { isGradebookFetching, isGradebookReFetching } = this.props;
    const isFetching = isGradebookFetching || isGradebookReFetching;
    return isFetching;
  };

  getFormattedPoints(pointsPossible) {
    const precision = !Number.isInteger(pointsPossible) ? 2 : 0;
    return round(pointsPossible, precision);
  }

  checkActivityVisibility(activity, studentId) {
    const visibilityStatus = this.getVisibilityStatusWithExceptions(
      activity,
      studentId,
    );
    switch (visibilityStatus) {
      case VISIBILITY_OPTIONS.ALL:
        return true;
      case VISIBILITY_OPTIONS.NONE:
        return false;
      case VISIBILITY_OPTIONS.DAYS_BEFORE:
      case VISIBILITY_OPTIONS.CUSTOM: {
        let date = _get(
          activity,
          'Exceptions[0].exception_value.visibility_start',
        );
        if (!date) {
          date = activity.visibility_available_date;
        }
        return dayjs().isAfter(date);
      }
      default:
        return true;
    }
  }

  getVisibilityStatusWithExceptions(activity, studentId) {
    let visibilityStatus = getActivityVisibilityStatus(activity);

    if (activity.exceptions?.length > 0) {
      const studentException = activity.exceptions.filter((ex) =>
        ex.user_ids.includes(studentId),
      );
      if (studentException?.length > 0) {
        const exceptionVisibility = this.getExceptionVisibility(
          activity,
          studentException[0],
        );
        visibilityStatus = getActivityVisibilityStatus(exceptionVisibility);
      }
    }
    return visibilityStatus;
  }

  getExceptionVisibility(activity, studentException) {
    return {
      ...activity,
      visibility_available_date: _get(
        studentException,
        'exception_value.visibility_start',
        null,
      ),
      visibility_until_date: _get(
        studentException,
        'exception_value.visibility_end',
        null,
      ),
      visibility: _get(studentException, 'visibility'),
    };
  }

  isActivityLinkDisabled(activity, studentId) {
    let visibilityStatus = this.getVisibilityStatusWithExceptions(
      activity,
      studentId,
    );

    if (
      visibilityStatus === VISIBILITY_OPTIONS.DAYS_BEFORE ||
      visibilityStatus === VISIBILITY_OPTIONS.CUSTOM
    ) {
      if (activity.exceptions?.length > 0) {
        const studentException = activity.exceptions.filter((ex) =>
          ex.user_ids.includes(studentId),
        );
        if (studentException?.length > 0) {
          const visibilityEndDateForException = _get(
            studentException[0],
            'exception_value.visibility_end',
          );
          return dayjs().isAfter(visibilityEndDateForException);
        }
      }
      return dayjs().isAfter(activity.visibility_until_date);
    }
    return false;
  }

  renderStudentTableAverage({ assignments }, categoryAssignments, weight = 0) {
    const { rdxGradebookSettings, roundPercentType } = this.props;
    const categoryTestCell = _get(
      categoryAssignments,
      '[0].gradebook_category_name',
      'unknownCategory',
    );

    if (categoryAssignments.length > 0) {
      const { pointsEarned, pointsPossible, grade } =
        calculateAssignmentsTotals(categoryAssignments);
      const formattedPointsPossible = this.getFormattedPoints(pointsPossible);
      let displayTotal = 0;
      if (this.areWeightsEnabled()) {
        // If weighted, display weighted category total
        const categoryTotal = getCalculatedGrade(pointsPossible, pointsEarned);
        const weightedCategoryTotal = categoryTotal * weight;
        displayTotal = formatDisplayPercentage(
          weightedCategoryTotal,
          roundPercentType,
        );
      } else {
        // If not weighted, display category score as percentage of total score
        const courseTotals = calculateAssignmentsTotals(assignments);
        const categoryPointPercentOfCourse = getCalculatedGrade(
          courseTotals.pointsPossible,
          pointsEarned,
        );
        displayTotal = formatDisplayGrade(
          categoryPointPercentOfCourse,
          { isAggregateCell: true },
          rdxGradebookSettings,
          null,
          roundPercentType,
        );
      }

      return (
        <tr data-test-id="student_gradebook_averages_row">
          <td className={`${s.allDevices}`}>
            <span className={s.totalLabel}>
              {this.getAssignmentCategoryName(categoryAssignments[0])} Total
            </span>
          </td>
          <td
            className={s.categorySummaryCell}
            data-test-id={`${categoryTestCell}-category-averages-percent`}
          >
            {formatDisplayGrade(
              grade,
              { isAggregateCell: true },
              rdxGradebookSettings,
              null,
              roundPercentType,
            )}
          </td>
          <td
            className={s.categorySummaryCell}
            data-test-id={`${categoryTestCell}-category-total-points`}
          >
            {formatDisplayPoints(
              { studentPoints: pointsEarned, isAggregateCell: true },
              rdxGradebookSettings,
              { precision: 2 },
            )}
            /{formattedPointsPossible}pts
          </td>
          <td
            className={s.categorySummaryCell}
            data-test-id={`${categoryTestCell}-category-total-percentage`}
          >
            {displayTotal}
          </td>
          <td />
          <td
            className={s.mobileCol}
            data-test-id="student_gradebook_of_total_grades_cell"
          >
            <span className={s.topGrade}>
              {formatDisplayGrade(
                grade,
                { isAggregateCell: true },
                rdxGradebookSettings,
              )}
            </span>
            <br />
            <span className={s.bottomGrade}>
              {formatDisplayPoints(
                { studentPoints: pointsEarned, isAggregateCell: true },
                rdxGradebookSettings,
                { precision: 0 },
              )}
              /{formattedPointsPossible}pts
            </span>
          </td>
        </tr>
      );
    }
    return (
      <tr>
        <td>Course Total</td>
        <td>
          {formatDisplayGrade(
            0.0,
            null,
            rdxGradebookSettings,
            null,
            roundPercentType,
          )}
        </td>
      </tr>
    );
  }

  renderFeedback(assignment, key) {
    const { course, rdxGradebookSettings, roundPercentType } = this.props;
    const formattedGrade = StudentGradebook.getStudentPointDisplay(assignment);
    const percentGrade = formatDisplayGrade(
      assignment.studentGrade,
      assignment,
      rdxGradebookSettings,
      null,
      roundPercentType,
    );
    const gradeCommentData = {
      ...assignment,
      course,
      percentGrade,
      grade: formattedGrade,
      assignedPoints: assignment.assigned_points,
      comment: _get(assignment, 'gradeComment', ''),
    };
    let gradeCommentButton;
    if (assignment.gradeComment) {
      gradeCommentButton = (
        <button
          key={`dropped-button-${key}`}
          className={s.gradeCommentButton}
          onClick={() =>
            this.props.showGradebookCommentDialog(gradeCommentData)
          }
        >
          View Feedback
        </button>
      );
    }

    if (assignment.isDropped && gradeCommentButton) {
      gradeCommentButton = (
        <Fragment>
          {gradeCommentButton}
          <span className={s.droppedBar}>
            {' '}
            |{' '}
            <span key={`dropped-span-${key}`} className={s.dropped}>
              Dropped
            </span>
          </span>
        </Fragment>
      );
    } else if (assignment.isDropped) {
      gradeCommentButton = (
        <span key={`dropped-span-${key}`} className={s.dropped}>
          Dropped
        </span>
      );
    }
    return gradeCommentButton;
  }

  renderStudentTableGrades(assignments, isUngradedCategory, studentId) {
    const {
      rdxGradebookSettings,
      roundPercentType,
      getLaunchUrl,
      handleLocalLaunch,
    } = this.props;
    if (assignments && assignments.length > 0) {
      // sort by ISO and filter assignments by visibility status
      const sortedAssignments = sortAssignmentsByISO(
        assignments,
        'studentAssignmentDueAt',
      );
      const filteredAssignments = _filter(sortedAssignments, (assignment) =>
        this.checkActivityVisibility(assignment, studentId),
      );
      const assignmentRows = _map(filteredAssignments, (assignment, i) => {
        let className = s.cellData;
        if (assignment.isDropped) {
          className = s.cellDataDropped;
        }
        const isActivityLinkDisabled = this.isActivityLinkDisabled(
          assignment,
          studentId,
        );
        const isOverdue = _get(assignment, 'isOverdue', false);

        return (
          <tr key={i} data-test-id="student_gradebook_row">
            <td className={`${s.cellData} ${s.mobileCol}`}>
              <div className={s.studentAssignmentContainer}>
                <AssignmentHeaderLabelAndTool
                  assignment={assignment}
                  assignmentNameStyle={s.AssignmentHeaderLink}
                  isLinkDisabled={isActivityLinkDisabled}
                  getLaunchUrl={getLaunchUrl}
                  handleLocalLaunch={handleLocalLaunch}
                  isStudent={true}
                />
                <DateCompleted assignment={assignment} />
                {isOverdue && <span className={s.overdueLabel}>Overdue</span>}
                <div>{this.renderFeedback(assignment, i)}</div>
              </div>
            </td>
            <td className={`${s.cellData}`}>
              <div className={s.studentAssignmentContainer}>
                <AssignmentHeaderLabelAndTool
                  assignment={assignment}
                  assignmentNameStyle={s.AssignmentHeaderLink}
                  isLinkDisabled={isActivityLinkDisabled}
                  getLaunchUrl={getLaunchUrl}
                  handleLocalLaunch={handleLocalLaunch}
                  isStudent={true}
                />
                <DateCompleted assignment={assignment} />
                {isOverdue && <span className={s.overdueLabel}>Overdue</span>}
              </div>
            </td>
            {!isUngradedCategory && (
              <React.Fragment>
                <td
                  className={className}
                  data-test-id="student_gradebook_percent_cell"
                >
                  {formatDisplayGrade(
                    assignment.studentGrade,
                    assignment,
                    rdxGradebookSettings,
                    null,
                    roundPercentType,
                  )}
                </td>
                <td
                  className={className}
                  data-test-id={`${assignment.name}-points`}
                >
                  {formatDisplayPoints(assignment, rdxGradebookSettings, {
                    precision: 2,
                  })}
                  /{this.getFormattedPoints(assignment.assigned_points)}pts
                </td>
                <td
                  className={className}
                  data-test-id={`${assignment.name}-total-percentage`}
                >
                  {formatDisplayGrade(
                    this.getAssignmentPointPercentOfCourse(
                      assignment,
                      assignments,
                    ),
                    assignment,
                    rdxGradebookSettings,
                    null,
                    roundPercentType,
                  )}
                </td>
              </React.Fragment>
            )}
            {isUngradedCategory && (
              <td
                className={className}
                data-test-id="student_gradebook_complete_incomplete_cell"
              >
                <UngradedIcon
                  status={assignment.status}
                  tool={assignment.tool}
                />
              </td>
            )}
            <td
              className={className}
              data-test-id="student_gradebook_feedback_cell"
            >
              {this.renderFeedback(assignment, i)}
            </td>
            <td className={`${className} ${s.mobileCol}`}>
              {!isUngradedCategory && (
                <React.Fragment>
                  <span className={`${s.topGrade}`}>
                    {formatDisplayGrade(
                      assignment.studentGrade,
                      assignment,
                      rdxGradebookSettings,
                      null,
                      roundPercentType,
                    )}
                  </span>
                  <br />
                  <span className={`${s.bottomGrade}`}>
                    {formatDisplayPoints(assignment, rdxGradebookSettings, {
                      precision: 0,
                    })}
                    /{this.getFormattedPoints(assignment.assigned_points)}pts
                  </span>
                </React.Fragment>
              )}
              {isUngradedCategory && (
                <UngradedIcon
                  status={assignment.status}
                  tool={assignment.tool}
                />
              )}
            </td>
          </tr>
        );
      });

      return assignmentRows;
    }

    return (
      <tr data-test-id="student_gradebook_no_grades">
        <td colSpan={isUngradedCategory ? 1 : 3} className={s.empty}>
          <p className={s.emptyViewTitle}>There are no grades to display</p>
          <p className={s.emptyViewText}>Check back soon.</p>
        </td>
      </tr>
    );
  }

  renderAlert() {
    const { alert } = this.props;
    return (
      <MLAlert
        show={alert.show}
        dismissTime={alert.dismissTime}
        dismissible={alert.dismissible}
        onDismiss={() => {
          this.props.updateAlert(emptyAlert);
        }}
        text={alert.text}
        alertType={alert.type}
      />
    );
  }

  renderCard(studentGradebook, categoryAssignments, categoryName) {
    const {
      categories,
      gradingType,
      currentStudent: { catToggle, mobileExpandedCategories },
    } = this.props;

    const isMobile = this.state.width <= MOBILE_WIDTH;

    const isCategoryExpandedOnMobile = mobileExpandedCategories.find(
      (catName) => catName === categoryName,
    );
    const isCatCollapsedForMobile = isMobile && !isCategoryExpandedOnMobile;

    let active = !_isEmpty(_find(catToggle, (i) => i === categoryName));

    if (isMobile) {
      active = isCatCollapsedForMobile;
    }

    const currentCategory = categories
      .concat({ id: -1, name: 'Ungraded', drop_lowest_num_grades: 0 })
      .find((category) => category.name === categoryName);
    const isUngradedCategory =
      currentCategory && currentCategory.is_default_ungraded;

    /*
     * TODO If we implement selectors on the StudentGradebook
     *      this logic should go there.
     */
    if (currentCategory && isNaN(currentCategory.weight)) {
      currentCategory.weight = 0;
    }

    return (
      <div
        className={s.studentTableCard}
        key={`category-${categoryName.replace(' ', '').toLowerCase()}`}
      >
        <table
          className={s.studentTable}
          role="grid"
          aria-readonly="true"
          tabIndex="0"
          aria-labelledby="studentTableTitle"
        >
          <thead>
            <tr data-test-id="student_gradebook_header_row">
              <th className={`${s.studentTableHeaderToggle} ${s.allDevices}`}>
                {currentCategory && (
                  <CategoryNameButton
                    active={active}
                    activeIcon="chevron_right"
                    inactiveIcon="chevron_down"
                    testId={'student-view-collapse-gradebook-category'}
                    category={currentCategory}
                    categoryNameClassName={s.categoryName}
                    showWeightLabel={
                      gradingType === GRADEBOOK_SETTINGS.gradingTypeWeighted
                    }
                    handleCategoryTabToggle={() => {
                      let mobileCats = mobileExpandedCategories;
                      const hasCatBeenToggled = _find(
                        catToggle,
                        (i) => i === categoryName,
                      );
                      const hasCatBeenExpandedForMobile =
                        mobileExpandedCategories.find(
                          (cat) => cat === categoryName,
                        );

                      // This will expand the cat for mobile and
                      // keep a consistent experience when resizing the window
                      if (
                        (isMobile && !hasCatBeenExpandedForMobile) ||
                        (!isMobile && hasCatBeenToggled)
                      ) {
                        mobileCats = _concat(
                          mobileExpandedCategories,
                          categoryName,
                        );
                      } else {
                        mobileCats = _without(mobileCats, categoryName);
                      }

                      if (hasCatBeenToggled) {
                        this.props.updateStudent({
                          catToggle: _without(catToggle, categoryName),
                          mobileExpandedCategories: mobileCats,
                        });
                      } else {
                        this.props.updateStudent({
                          catToggle: _concat(catToggle, categoryName),
                          mobileExpandedCategories: mobileCats,
                        });
                      }
                    }}
                  />
                )}
              </th>
              {!isUngradedCategory && <th>Percentage</th>}
              {!isUngradedCategory && <th>Points</th>}
              {!isUngradedCategory && <th>Percent of Total Grades</th>}
              {isUngradedCategory && <th>Complete/Incomplete</th>}
              <th>
                <div className={s.instructorFeedbackHead}>
                  Instructor
                  <br />
                  Provided Feedback
                </div>
              </th>
              <th className={`${s.mobileCol} ${s.performance}`}>
                {isUngradedCategory ? 'Complete' : 'Performance'}
              </th>
            </tr>
          </thead>
          {active || (
            <tbody>
              {this.renderStudentTableGrades(
                categoryAssignments,
                isUngradedCategory,
                studentGradebook.studentId,
              )}
            </tbody>
          )}
          {!isUngradedCategory && (
            <tfoot>
              {this.renderStudentTableAverage(
                studentGradebook,
                categoryAssignments,
                _get(currentCategory, 'weight'),
              )}
            </tfoot>
          )}
        </table>
      </div>
    );
  }

  renderStudentTable(studentGradebook) {
    const assignments = _get(
      studentGradebook,
      'assignments',
      this.props.assignments,
    );

    let sortedCategories = this.props.categories || [];

    const assignmentsByCategory = groupAssignmentsByCategory(
      sortedCategories,
      assignments,
    );

    sortedCategories = sortedCategories.filter(
      (category) =>
        assignmentsByCategory[category.id] &&
        assignmentsByCategory[category.id].length,
    );

    return (
      <div className={s.studentCardWrapper}>
        {_map(sortedCategories, (category) =>
          this.renderCard(
            studentGradebook,
            assignmentsByCategory[category.id],
            category.name,
          ),
        )}
      </div>
    );
  }

  renderGradesHeader() {
    const {
      studentGradebook,
      categories,
      categoryWeights,
      settings,
      roundPercentType,
    } = this.props;
    const { assignments } = studentGradebook;

    const { pointsEarned, pointsPossible, grade } = getTotals(
      assignments,
      categoryWeights,
      settings,
      categories,
    );

    return (
      <div className={s.headerRow}>
        <h2 className={s.studentTableTitle} id="studentTableTitle">
          My Grades
        </h2>
        <div className={s.courseTotalLabel}>Course Total</div>
        <div
          data-test-id="student_gradebook_total_percent"
          className={s.gradesCallout}
        >
          {formatDisplayGrade(grade, null, null, null, roundPercentType)}
        </div>
        {!this.areWeightsEnabled() && (
          <div
            data-test-id="student_gradebook_total_points"
            className={s.pointsOf}
          >
            <strong>{this.getFormattedPoints(pointsEarned)}</strong>
            <span>out of {this.getFormattedPoints(pointsPossible)}</span>
          </div>
        )}
      </div>
    );
  }

  renderStudentInformation() {
    return (
      <div className={s.informationRow}>
        <span className={s.loudspeaker}>
          <MLIcon
            type="loudspeaker"
            alt={'loudSpeaker'}
            className={s.loudspeakerStyle}
          />
        </span>
        <span className={s.information}>
          Looking for a grade? Your scores will appear after the due date.
        </span>
      </div>
    );
  }

  renderStudentId() {
    const { settings, user, showEditStudentId } = this.props;
    let thirdPartyUserId;
    if (user.thirdPartyUserId) thirdPartyUserId = user.thirdPartyUserId;
    else if (user.user_metadata && user.user_metadata.thirdPartyUserId)
      thirdPartyUserId = user.user_metadata.thirdPartyUserId;
    const studentId = extractSettingAs(
      settings,
      'studentIdCalled',
      (setting) => setting.value,
    );

    return (
      <span>
        | {studentId}: {thirdPartyUserId || 'N/A'} (
        <button
          className={s.thirdPartyIdEditButton}
          onClick={showEditStudentId}
          aria-label="Edit Student ID"
        >
          Edit
        </button>
        )
      </span>
    );
  }

  renderStudentIdSection() {
    const { user } = this.props;
    const studentName = _get(user, 'name');

    return (
      <div>
        <div className={s.divider} />
        <div className={s.studentName}>
          {studentName} {this.renderStudentId()}
        </div>
      </div>
    );
  }

  renderLoading = () => (
    <div className={s.spinnerContainer}>
      <MLLoading />
    </div>
  );

  render() {
    const {
      studentGradebook,
      courseId,
      user,
      alert,
      settings,
      course,
      getLaunchUrl,
      handleLocalLaunch,
    } = this.props;

    const isFetching = this.isFetching();
    const isReefLinked = _get(user, 'isReefLinked');
    const requireStudentId = _get(
      _find(settings, (setting) => setting.key === 'requireStudentId'),
      'value',
    );
    const isStudentIdRequired = String(requireStudentId) === 'true';

    const gradesToReport = extractSettingAs(
      this.props.rdxGradebookSettings,
      'gradesToReport',
      (setting) => parseInt(setting.value, 10),
    );
    const isReportOnlyDueDatePassed =
      gradesToReport === GRADEBOOK_SETTINGS.reportOnlyDueDatePassedAssignments;

    const { FF_STUDENT_GOAL_REFLECTION } = getConfigObject();

    // Session user id — used for dashboards GRS requests
    const studentId = user.user_id;

    // Gradebook payload student id - to keep consistent with student gb
    const visibilityStudentId = studentGradebook.studentId;
    const weeklyProgressAssignments = _filter(
      _get(studentGradebook, 'assignments', this.props.assignments) || [],
      (assignment) =>
        this.checkActivityVisibility(assignment, visibilityStudentId),
    );

    return (
      <React.Fragment>
        {alert && alert.show && this.renderAlert()}
        <div className={s.studentTableWrapper}>
          {!isFetching && this.renderGradesHeader()}
          {!isFetching &&
            isReportOnlyDueDatePassed &&
            this.renderStudentInformation()}
          {isStudentIdRequired && this.renderStudentIdSection()}
          {FF_STUDENT_GOAL_REFLECTION && (
            <StudentGoalReflectionContainer
              courseId={courseId}
              studentId={studentId}
            />
          )}
          <WeeklyProgress
            course={course}
            assignments={weeklyProgressAssignments}
            settings={settings}
            getLaunchUrl={getLaunchUrl}
            handleLocalLaunch={handleLocalLaunch}
          />
          {isFetching
            ? this.renderLoading()
            : this.renderStudentTable(studentGradebook)}
          {isReefLinked && (
            <MLButton
              btnType="secondary"
              className={s.iClickerButton}
              onClick={() => {
                this.props.unlinkReefStudent(courseId);
              }}
              testId="btn_disconnect_iclicker"
            >
              <MLIcon
                type="iclicker"
                viewBox="0 0 85 85"
                className={s.headerBtnIcon}
              />
              <span data-test-id="iclicker-menu-text">Disconnect iClicker</span>
            </MLButton>
          )}
        </div>
      </React.Fragment>
    );
  }
}

export default StudentGradebook;
